import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Upload, MessageSquare, Library, MapPin, FilePlus, Building2, TrendingUp, FileText, BookOpen, Home, Shield, LogOut, Sun, Moon, MessagesSquare } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../auth/AuthContext';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  href: string;
}

const navItems: NavItem[] = [
  { id: 'update-feed', label: 'Feed',    icon: Upload,        href: '/update-feed' },
  { id: 'nam-ai',      label: 'AI Chat', icon: MessageSquare, href: '/nam-ai' },
  { id: 'library',     label: 'Library', icon: Library,       href: '/library' },
  { id: 'entities',    label: 'Entities', icon: Building2,    href: '/entities' },
  { id: 'map',         label: 'Map',     icon: MapPin,        href: '/map' },
  { id: 'trends',      label: 'Trends',  icon: TrendingUp,    href: '/analytics-dashboard' },
  { id: 'reports',     label: 'Reports', icon: FileText,      href: '/reports' },
  { id: 'standards',   label: 'Guides',  icon: BookOpen,      href: '/standards' },
];

// The Admin flyout renders through a portal on <body> rather than inside the
// sidebar. The <aside> sets backdrop-filter, which creates a stacking context and
// traps any z-index set inside it below later page content — and the Map HUD
// panels sit at z-index 400-600 regardless. Portalling to <body> with a fixed
// position lifts the menu out of that context so it is always on top.
const FLYOUT_Z = 1000;
const FLYOUT_GAP = 10;      // horizontal gap between the rail and the menu
const VIEWPORT_MARGIN = 8;  // keep the menu clear of the window edge

export function MinimalSidebar() {
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { isCAUser, isAdmin, isMember } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const adminBtnRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const adminActive = pathname === '/admin' || pathname.startsWith('/pdf-upload') || pathname.startsWith('/chat-history');
  // CA-leadership users get a "My CA" home item first; everyone keeps the rest.
  // Members have no Reports access, so the item is hidden for them (the Admin
  // flyout below is already admin-only, so it never shows for a member).
  const visible = isMember ? navItems.filter(n => n.id !== 'reports') : navItems;
  const items: NavItem[] = isCAUser
    ? [{ id: 'ca-home', label: 'My CA', icon: Home, href: '/ca-home' }, ...visible]
    : visible;
  const activeId = (items.find(n => n.href === pathname)
    || items.find(n => n.href !== '/' && pathname.startsWith(n.href + '/')))?.id
    ?? (adminActive ? '' : (isCAUser ? 'ca-home' : 'update-feed'));

  // Anchor the portalled menu to the button, clamped so it never runs off-screen.
  const positionFlyout = useCallback(() => {
    const btn = adminBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const h = flyoutRef.current?.offsetHeight ?? 0;
    const maxTop = window.innerHeight - h - VIEWPORT_MARGIN;
    setFlyoutPos({
      top: Math.max(VIEWPORT_MARGIN, Math.min(r.top, maxTop)),
      left: r.right + FLYOUT_GAP,
    });
  }, []);

  // Measure before paint so the menu never appears at the wrong spot first.
  useLayoutEffect(() => { if (adminOpen) positionFlyout(); }, [adminOpen, positionFlyout]);

  useEffect(() => {
    if (!adminOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAdminOpen(false); };
    window.addEventListener('resize', positionFlyout);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', positionFlyout);
      window.removeEventListener('keydown', onKey);
    };
  }, [adminOpen, positionFlyout]);

  // Close the menu on navigation, so it never lingers over the page it opened.
  useEffect(() => { setAdminOpen(false); }, [pathname]);

  return (
    <aside style={{
      width: '72px',
      minWidth: '72px',
      height: '100vh',
      background: 'var(--glass-bg)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRight: '1px solid var(--glass-border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: '20px',
      paddingBottom: '20px',
      gap: '0',
    }}>
      {/* Logo mark */}
      <div style={{
        width: '36px',
        height: '36px',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '32px',
        flexShrink: 0,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.5 0 8 4 3 4 5.5 0 7-.5 1.5 1 3 1 3"/>
          <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
        </svg>
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '0 8px' }}>
        {items.map(({ id, label, icon: Icon, href }) => {
          const active = activeId === id;
          return (
            <Link
              key={id}
              to={href}
              title={label}
              style={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '44px',
                borderRadius: '10px',
                color: active ? 'var(--accent)' : 'var(--text-muted)',
                background: active ? 'var(--accent-dim)' : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                }
              }}
            >
              {active && (
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '24px',
                  background: 'var(--accent)',
                  borderRadius: '0 3px 3px 0',
                }} />
              )}
              <Icon size={18} strokeWidth={active ? 2.5 : 2} />
            </Link>
          );
        })}

        {/* Admin (admins only): button opens a flyout with Admin + PDF Upload */}
        {isAdmin && (
          <div style={{ position: 'relative', width: '100%' }}>
            <button
              ref={adminBtnRef}
              type="button"
              title="Admin"
              aria-haspopup="menu"
              aria-expanded={adminOpen}
              onClick={() => setAdminOpen(o => !o)}
              style={{
                position: 'relative', width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', height: '44px', borderRadius: '10px', border: 'none',
                cursor: 'pointer', transition: 'all 0.15s ease',
                color: adminActive || adminOpen ? 'var(--accent)' : 'var(--text-muted)',
                background: adminActive || adminOpen ? 'var(--accent-dim)' : 'transparent',
              }}
              onMouseEnter={e => { if (!(adminActive || adminOpen)) { (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)'; } }}
              onMouseLeave={e => { if (!(adminActive || adminOpen)) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'; } }}
            >
              {adminActive && (
                <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '3px', height: '24px', background: 'var(--accent)', borderRadius: '0 3px 3px 0' }} />
              )}
              <Shield size={18} strokeWidth={adminActive ? 2.5 : 2} />
            </button>

            {adminOpen && createPortal(
              <>
                {/* click-away overlay — directly beneath the menu, above everything else */}
                <div onClick={() => setAdminOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: FLYOUT_Z - 1 }} />
                <div ref={flyoutRef} role="menu" style={{
                  position: 'fixed', top: flyoutPos.top, left: flyoutPos.left, zIndex: FLYOUT_Z,
                  background: 'var(--surface)', border: '1px solid var(--glass-border)', borderRadius: '10px',
                  padding: '6px', minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '2px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
                }}>
                  {([
                    { to: '/admin', label: 'Admin', Icon: Shield },
                    { to: '/pdf-upload', label: 'PDF Upload', Icon: FilePlus },
                    { to: '/chat-history', label: 'Chat History', Icon: MessagesSquare },
                  ] as const).map(({ to, label, Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setAdminOpen(false)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                        borderRadius: '8px', fontSize: '13px', textDecoration: 'none', whiteSpace: 'nowrap',
                        color: pathname.startsWith(to) ? 'var(--accent)' : 'var(--text-secondary)',
                        background: pathname.startsWith(to) ? 'var(--accent-dim)' : 'transparent',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = pathname.startsWith(to) ? 'var(--accent-dim)' : 'transparent'; }}
                    >
                      <Icon size={15} /> {label}
                    </Link>
                  ))}
                </div>
              </>,
              document.body,
            )}
          </div>
        )}
      </nav>

      {/* Bottom: user + logout */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '0 8px', width: '100%' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: 'var(--accent-dim)',
          border: '1px solid var(--accent-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--accent)',
        }}>
          U
        </div>
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            width: '100%',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)';
            (e.currentTarget as HTMLElement).style.color = 'var(--accent)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
          }}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <button
          onClick={async () => {
            try {
              await fetch('/update-feed/logout', { method: 'POST' });
            } catch (err) {
              console.error('Logout error:', err);
            } finally {
              window.location.href = '/login';
            }
          }}
          title="Logout"
          style={{
            width: '100%',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = 'var(--danger-dim)';
            (e.currentTarget as HTMLElement).style.color = 'var(--danger-strong)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
          }}
        >
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
